import { AfterContentChecked, Component, ElementRef, Renderer2, inject, contentChild, contentChildren, input } from '@angular/core';
import { NgControl } from '@angular/forms';

import { SERVER_ERROR_KEY } from '../../common/server-field-errors';
import { errorsDue, FieldErrorDirective, FieldErrorsComponent } from './field-errors.component';

/**
 * One labelled form field (#199): label, the projected control, its red border and its messages.
 * It finds the control itself through the projected `formControlName`, so the field keeps its
 * own `id`, testid and attributes:
 *
 *   <app-form-row label="Ort" i18n-label>
 *     <input id="city" formControlName="city" class="form-control form-control-sm" />
 *   </app-form-row>
 *
 * The border and the messages appear together, once the field was touched or typed in (#199
 * decision 2a) — a fresh form no longer opens with its empty required fields in red. A server
 * error (#178) shows at once. The column classes default to the label-left layout most forms use.
 */
@Component({
  selector: 'app-form-row',
  imports: [FieldErrorsComponent],
  template: `
    <div [class]="rowClass()">
      <label [class]="labelClass()" [attr.for]="fieldId">{{ label() }}</label>
      <div [class]="fieldClass()">
        <ng-content></ng-content>
        <app-field-errors [control]="ngControl()?.control" [custom]="customErrors"></app-field-errors>
      </div>
    </div>
  `,
  // Block, so the row behaves as the `div` it replaces: inside a Bootstrap `.row` it takes the
  // `.row > *` gutter that div used to take, anywhere else it simply stacks.
  styles: [':host { display: block; }'],
})
export class FormRowComponent implements AfterContentChecked {
  private renderer = inject(Renderer2);

  readonly label = input<string>();
  readonly rowClass = input('row mb-2');
  readonly labelClass = input('col-sm-4 col-form-label col-form-label-sm');
  readonly fieldClass = input('col-sm-8');

  readonly ngControl = contentChild(NgControl);
  private readonly fieldElement = contentChild(NgControl, { read: ElementRef });
  private readonly errorTemplates = contentChildren(FieldErrorDirective);

  get customErrors(): readonly FieldErrorDirective[] {
    return this.errorTemplates();
  }

  get fieldId(): string | null {
    return this.fieldElement()?.nativeElement.id || null;
  }

  // Bootstrap's `is-invalid` belongs on native fields only; Kendo widgets style their own
  // `ng-invalid ng-touched` state.
  ngAfterContentChecked(): void {
    const element = this.fieldElement()?.nativeElement;
    if (!element || !/^(INPUT|SELECT|TEXTAREA)$/.test(element.tagName)) return;
    const control = this.ngControl()?.control;
    const invalid = errorsDue(control) || !!control?.hasError(SERVER_ERROR_KEY);
    if (invalid) this.renderer.addClass(element, 'is-invalid');
    else this.renderer.removeClass(element, 'is-invalid');
  }
}
